import { useCallback, useEffect, useRef, useState } from 'react';

export type Slide = { image: string } & Record<string, unknown>;

export function useSliderLogic(
  slides: Slide[],
  autoplayInterval: number,
  shouldLoad: boolean
) {
  const images = slides.map(s => s.image);
  const total = images.length;
  const [current, setCurrent] = useState(0);
  const [isAutoplaying, setIsAutoplaying] = useState(true);
  const [loaded, setLoaded] = useState<boolean[]>(() =>
    images.map((_, i) => i === 0)
  );

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const stopAutoplay = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const startAutoplay = useCallback(() => {
    stopAutoplay();
    if (!shouldLoad || total <= 1) return;
    timeoutRef.current = setTimeout(() => {
      setCurrent(i => (i + 1) % total);
    }, autoplayInterval);
  }, [autoplayInterval, shouldLoad, total]);

  useEffect(() => {
    if (isAutoplaying) startAutoplay();
    return stopAutoplay;
  }, [current, isAutoplaying, startAutoplay]);

  useEffect(() => {
    setCurrent(0);
    setIsAutoplaying(true);
    setLoaded(images.map((_, i) => i === 0));
  }, [images.join('|')]);

  const ensureLoaded = useCallback(
    (idx: number) => {
      if (!shouldLoad) return;
      if (idx < 0 || idx >= total) return;
      if (loaded[idx] || !images[idx]) return;
      const img = new Image();
      const src = images[idx];
      img.src = src;
      img.onload = () =>
        setLoaded(prev => {
          if (prev[idx]) return prev;
          const copy = [...prev];
          copy[idx] = true;
          return copy;
        });
    },
    [images, loaded, shouldLoad, total]
  );

  useEffect(() => {
    ensureLoaded(current);
  }, [current, ensureLoaded]);

  useEffect(() => {
    if (!shouldLoad || total <= 1) return;
    const id = setTimeout(() => {
      const nextIdx = (current + 1) % total;
      ensureLoaded(nextIdx);
    }, 5000);
    return () => clearTimeout(id);
  }, [current, total, ensureLoaded, shouldLoad]);

  const primeNeighbors = useCallback(() => {
    if (total <= 1) return;
    ensureLoaded((current + 1) % total);
    ensureLoaded((current - 1 + total) % total);
  }, [current, total, ensureLoaded]);

  const goToSlide = (idx: number) => {
    ensureLoaded(idx);
    setCurrent(idx);
    setIsAutoplaying(false);
  };

  const goToPrev = () => {
    const idx = (current - 1 + total) % total;
    ensureLoaded(idx);
    setCurrent(idx);
    setIsAutoplaying(false);
  };

  const goToNext = () => {
    const idx = (current + 1) % total;
    ensureLoaded(idx);
    setCurrent(idx);
    setIsAutoplaying(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    stopAutoplay();
    setIsAutoplaying(false);
    if (!e.changedTouches?.[0]) return;
    touchStartX.current = e.changedTouches[0].clientX;

    primeNeighbors();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) > 50) {
      if (distance > 0) goToNext();
      else goToPrev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return {
    current,
    isAutoplaying,
    setIsAutoplaying,
    goToPrev,
    goToNext,
    goToSlide,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    loaded,
    images,
    total
  };
}
